import { GetStaticPaths, GetStaticPropsContext, InferGetStaticPropsType } from 'next';
// eslint-disable-next-line import/named
import { v4 as uuid } from 'uuid';

import { CHANNEL, overviewPageSlugs } from '@boss/constants/b2b-b2c';
import { getSlugMap } from '@boss/contentful-client/b2b-b2c';
import { useRouter } from '@boss/hooks';
import { content as contentService } from '@boss/services';
import type { Environment, LOCALE_CODE } from '@boss/types/b2b-b2c';
import { Presence } from '@boss/ui';
import { getLastPartOfPath, isArrayWithContent } from '@boss/utils';

// FIXME: This is a temporary solution to ignore the enforce-module-boundaries rule that prevents circular dependencies
// eslint-disable-next-line @nx/enforce-module-boundaries
import { defaultLocale } from '../../../config/locales.config';
import { ComponentMapper, LoginSection, SEO } from '../components';
import { IS_BUILD, PageTypes } from '../constants';
import { useProfile } from '../hooks';
import Layout from '../layout/default';
import { getRedirectDestinationSlugs, homePageConfig, overviewPageTypes } from '../utils';
import { getAdditionalPageData } from '../utils/cms';
import getServerSideTranslations from '../utils/getServerSideTranslations';
import { mapContentfulToNextLocale, mapNextToContentfulLocale } from '../utils/localeMapper';
import { getMappedNavigation } from '../utils/navigation';

export const config = {
  maxDuration: 30,
};

const checkOverviewPageTypeExists = (overviewPageSlug: string | undefined, locale: string): boolean => {
  return (
    !!overviewPageSlug &&
    overviewPageTypes.some(
      type => overviewPageSlugs[type as keyof typeof overviewPageSlugs]?.[locale] === overviewPageSlug,
    )
  );
};

const constructOverviewPage = (
  overviewPageSlug: string | undefined,
  locale: string,
): {
  slug: string;
  type: string;
} | null => {
  if (!overviewPageSlug) {
    return null;
  }
  const type = overviewPageTypes.find(
    type => overviewPageSlugs[type as keyof typeof overviewPageSlugs]?.[locale] === overviewPageSlug,
  );

  return type ? { slug: overviewPageSlug, type } : null;
};

// TODO: This template could collide with other templates used.
// In this method there should be a check to check for forbidden patterns
// These should also be marked as reserved paths in contentful as well
export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const slugMap = await getSlugMap({
    preview: false,
    channel: CHANNEL,
    contentType: 'contentPage',
  });

  const getPageOverviewPrefix = (locale: string, tags: string[]) => {
    if (tags?.length && overviewPageTypes.some(type => tags.includes(type))) {
      const overviewPageType = overviewPageTypes.find(type => tags.includes(type)) as keyof typeof overviewPageSlugs;

      if (overviewPageType && overviewPageSlugs[overviewPageType]) {
        return overviewPageSlugs[overviewPageType][locale];
      }
    }

    return '';
  };

  const pages = slugMap
    // TODO: Find a more scaleble way to lock this scenario
    .filter(
      ({ slug, locale }) =>
        !['home', 'stores', 'roadmap', 'p', 'c', 'api', 'inspiratiebeelden', '404'].includes(slug) &&
        locales?.includes(mapContentfulToNextLocale(locale)),
    )
    .map(({ slug, locale, tags }) => {
      const nextLocale = mapContentfulToNextLocale(locale);
      const pagePrefix = tags?.length ? getPageOverviewPrefix(nextLocale, tags) : undefined;

      return {
        params: {
          slug: pagePrefix ? [pagePrefix, slug] : [slug],
        },
        locale: nextLocale,
      };
    });

  return {
    paths: pages,
    /* 
      We need to set fallback to blocking, so that new pages can be previewed. 
      The 404 handling will take place in the getStaticProps function.
    */
    fallback: 'blocking',
  };
};

export const getStaticProps = async ({
  preview,
  params,
  locale,
  locales,
}: GetStaticPropsContext<{ slug: string; locale: LOCALE_CODE }>) => {
  if (!locale) {
    return {
      notFound: true,
    };
  }

  const _locale = locale === 'default' ? defaultLocale : locale;
  const contentfulLocale = mapNextToContentfulLocale(_locale);

  const pagePathLength = params?.slug?.length ?? 0;
  const slug = params?.slug[pagePathLength - 1];
  const overviewPageSlug = pagePathLength === 2 ? params?.slug[0] : undefined;

  if (
    !slug ||
    slug === 'home' ||
    pagePathLength > 2 ||
    (pagePathLength === 2 && !checkOverviewPageTypeExists(overviewPageSlug, locale))
  ) {
    return {
      notFound: true,
    };
  }

  const [pageData, navigation] = await Promise.all([
    contentService.getContentPageBySlug({
      preview: !!preview,
      slug: getLastPartOfPath(slug),
      locale: contentfulLocale as LOCALE_CODE,
      channel: CHANNEL,
      defaultLocale,
      allLocales: locales?.filter(locale => locale !== 'default').map(locale => mapNextToContentfulLocale(locale)),
      batchMode: IS_BUILD,
    }),
    getMappedNavigation(contentfulLocale, !!preview),
  ]);

  const overviewPage = constructOverviewPage(overviewPageSlug, locale);

  const isOverviewPage = pageData?.tags?.some(tag => overviewPageTypes.includes(tag as keyof typeof overviewPageSlugs));

  if (
    !pageData ||
    !pageData?.publishForLocales?.includes(contentfulLocale as LOCALE_CODE) ||
    (overviewPage?.type && !pageData?.tags?.includes(overviewPage?.type)) ||
    (isOverviewPage && !overviewPageSlug)
  ) {
    return {
      notFound: true,
    };
  }

  const additionalData = await getAdditionalPageData(pageData.content, contentfulLocale);

  return {
    props: {
      pageType: isOverviewPage ? PageTypes.OVERVIEW_PAGE : PageTypes.CONTENT,
      preview: !!preview,
      locale: _locale,
      overviewPage,
      slug,
      pageData: pageData ?? {},
      additionalData: additionalData ?? {},
      environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
      navigation,
      ...(await getServerSideTranslations(_locale, ['common', 'stores', 'routes', 'forms'])),
    },
  };
};

export const Index = ({
  pageData,
  environment,
  preview,
  slug,
  navigation,
  locale,
  additionalData,
  overviewPage,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { showLoginSection, redirect: homePageRedirect } = homePageConfig;
  const { asPath, push } = useRouter();
  const { isLoggedIn } = useProfile();
  const crumbles = [{ href: slug, label: pageData.pageTitle }]?.filter(v => !!v.href); // Exclude crumbles without href like the homepage

  if (overviewPage) {
    crumbles.unshift({
      href: overviewPage?.slug,
      label: overviewPage?.slug,
    });
  }

  const mapTranslatedSlugs = (translatedSlugs: Record<string, string>) => {
    return Object.keys(translatedSlugs).reduce((acc, key) => {
      const locale = mapContentfulToNextLocale(key);
      const slug = translatedSlugs[key];

      if (overviewPage) {
        const translatedOverviewPage = overviewPageSlugs[overviewPage.type as keyof typeof overviewPageSlugs][locale];

        acc[locale] = `${translatedOverviewPage}${slug.startsWith('/') ? slug : '/' + slug}`;
      } else {
        acc[locale] = slug;
      }

      return acc;
    }, {} as Record<string, string>);
  };

  const localizedSlugs = mapTranslatedSlugs(pageData.translatedSlugs);

  const isHomePage = asPath === '/';
  const redirectDestination = isHomePage && getRedirectDestinationSlugs(homePageRedirect?.destination);

  if (!!homePageRedirect?.auth === isLoggedIn && redirectDestination) {
    push(redirectDestination[locale]);
  }

  return (
    <SEO
      breadcrumbs={crumbles}
      description={pageData.metaDescription}
      imageSrc={pageData.ogImage?.image?.file?.url}
      localizedSlugs={localizedSlugs}
      navigation={navigation}
      noFollow={pageData.noFollow}
      noIndex={pageData.noIndex}
      title={pageData.pageTitle}
    >
      <Layout
        crumbles={crumbles}
        environment={environment}
        localizedSlugs={localizedSlugs}
        navigation={navigation}
        preview={preview}
      >
        <Presence id="login-section-presence" visible={isHomePage && showLoginSection}>
          <LoginSection visible="anonymous" />
        </Presence>

        {isArrayWithContent(pageData?.content) &&
          pageData.content.map(entry => (
            <ComponentMapper
              colors={additionalData.colors}
              entry={entry}
              key={uuid()}
              locale={locale}
              products={additionalData.products}
              theme={pageData.theme}
            />
          ))}
      </Layout>
    </SEO>
  );
};

export default Index;
